import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import Card from "../components/common/Card";
import SecurityTrustNotice from "../components/common/SecurityTrustNotice";
import { useAuth } from "../contexts/AuthContext";

const errorMap = {
  "auth/email-already-in-use": "This email is already in use.",
  "auth/weak-password": "Password must be at least 8 characters.",
  "auth/invalid-email": "Please enter a valid email address.",
};

export default function SignupPage() {
  const { user, signUpUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    accessCode: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordMismatch = useMemo(
    () => form.confirmPassword && form.confirmPassword !== form.password,
    [form.confirmPassword, form.password]
  );

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (passwordMismatch) return;
    setLoading(true);
    setError("");
    try {
      await signUpUser(form.email, form.password, form.fullName, form.accessCode);
      navigate("/dashboard");
    } catch (err) {
      setError(errorMap[err.code] || "Unable to create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page page-enter">
      <Card style={{ width: "min(480px, 100%)" }}>
        <h1 className="h1" style={{ marginBottom: 18 }}>
          Create Account
        </h1>
        <form onSubmit={onSubmit}>
          <div className="form-row">
            <label>Full Name</label>
            <input required value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Email Address</label>
            <input type="email" required value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="form-row">
            <label>Password</label>
            <input
              type="password"
              minLength={8}
              required
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            />
          </div>
          <div className="form-row">
            <label>Confirm Password</label>
            <input
              type="password"
              required
              value={form.confirmPassword}
              onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
            />
            {passwordMismatch ? <div className="error-text">Passwords must match.</div> : null}
          </div>
          <div className="form-row">
            <label>Access Code</label>
            <input
              type="password"
              placeholder="Have an access code? Enter it here"
              value={form.accessCode}
              onChange={(e) => setForm((p) => ({ ...p, accessCode: e.target.value }))}
            />
          </div>
          {error ? <div className="error-text">{error}</div> : null}
          <Button className="full-width" type="submit" disabled={loading || passwordMismatch}>
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>
        <p className="muted" style={{ marginTop: 14 }}>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </Card>
      <SecurityTrustNotice />
    </div>
  );
}
